import { Avatar, Button, Listbox, ListboxItem, Popover, PopoverContent, PopoverTrigger, Tooltip } from '@nextui-org/react';
import { Icon } from '@iconify/react';
import { RootState, useGetFgaUserResourcesQuery, useGetUserOrgsQuery, useTenantDetails } from '@circuitry-ai/doc-data';
import { useSelector } from 'react-redux';
import { getUserInitials } from '../utils/commonutils';
import { CtyButton } from '../button/CtyButton';
import { useNavigate } from 'react-router-dom';
import { UserRolesBadges } from './UserRolesBadges';

interface SwitchOrgsProps {
    isCollapsed: boolean;
    doLogout: () => void;
    doLogin?: () => void;
    toggleSideNav: () => void;
    switchOrganisation: (name: string) => void;
    palView: boolean;
}

interface OrgData {
    display_name: string,
    id: string,
    metadata: {
        cloud_setup_status: string,
        tenant_id: string;
    },
    name: string;
}

const SwitchOrgs = (props: SwitchOrgsProps) => {

    const { isCollapsed, doLogout, doLogin, toggleSideNav, switchOrganisation, palView = false } = props;
    const { details: user } = useSelector((state: RootState) => state.auth.user);
    const token = useSelector((state: RootState) => state.auth.token);
    const navigate = useNavigate();
    const { tenantDetails } = useTenantDetails({ enabled: token ? true : false });
    const enableRolesInSidebar = tenantDetails?.meta_data?.enable_roles === true;
    const { data: userOrgsList, isLoading: isUserOrgsLoading } = useGetUserOrgsQuery(user?.sub, { skip: !user?.sub || !token });
    const { data: fgaUserResources } = useGetFgaUserResourcesQuery(user?.sub ?? '', {
        skip: !user?.sub || !token || !enableRolesInSidebar,
    });
    const userGroups: string[] = fgaUserResources?.data?.groups ?? [];

    if (isUserOrgsLoading) return;
    const userInitial = getUserInitials(user?.name);

    const userAuthAction = () => {
        if (user?.sub) {
            doLogout();
        } else {
            doLogin && doLogin();
        }
    };

    const userDetails = {
        name: (user?.name ?? 'Guest').length > 15 ? `${(user?.name ?? 'Guest').substring(0, 15)}...` : (user?.name ?? 'Guest'),
        org: (user?.org_name ?? '').length > 15 ? `${(user?.org_name ?? '').substring(0, 15)}...` : (user?.org_name ?? '')
    };

    const userProfileNavigate = () => {
        navigate(`/settings/profile`);
    }

    return (
        <div className="border-t-2  py-2 cursor-pointer">
            <Popover placement={'top'} >
                <PopoverTrigger>
                    {isCollapsed ?
                        <div className='py-2 flex items-center justify-center'>
                            <Button isIconOnly radius='full' onPress={toggleSideNav}>
                                <Avatar
                                    size={'md'}
                                    showFallback
                                    src={user?.picture ?? 'https://images.unsplash.com/broken'}
                                    name={userInitial}
                                    alt="Profile"
                                    classNames={{ base: 'bg-[#f0f1f2] text-default-600' }}
                                />
                            </Button>
                        </div>
                        :
                        <div className='flex flex-row items-center justify-between bg-rsed-300 w-full p-3'>
                            <Tooltip content={'Switch Organization'} color="warning" showArrow>
                                <div className='flex min-w-0 flex-1 flex-row gap-1.5 items-center'>
                                    <Avatar
                                        size='sm'
                                        showFallback
                                        src={user?.picture ?? 'https://images.unsplash.com/broken'}
                                        name={userInitial}
                                        alt="Profile"
                                        classNames={{ base: 'bg-[#f0f1f2] text-default-600' }}
                                    />
                                    <div className="min-w-0 flex-1">
                                        <div className='font-poppins text-sm font-medium leading-5 text-left truncate'>{userDetails["name"]}</div>
                                        <div className="font-poppins text-sm font-normal leading-5 text-left truncate">{userDetails["org"]}</div>
                                        {enableRolesInSidebar && (
                                            <UserRolesBadges groups={userGroups} className="mt-1" />
                                        )}
                                    </div>
                                </div>
                            </Tooltip>
                            <div className="shrink-0">
                                {!palView && <CtyButton
                                    tooltipContent={'Profile'}
                                    toolTipColor='warning'
                                    id={`user-profile`}
                                    isIconOnly
                                    radius="full"
                                    size="sm"
                                    variant="light"
                                    onPress={userProfileNavigate}
                                >
                                    <Icon icon="mdi:slider" width="24" height="24" style={{ color: "#667085" }} />
                                </CtyButton>}
                                <CtyButton
                                    tooltipContent={user?.sub ? 'Logout' : 'Login'}
                                    toolTipColor='warning'
                                    id={`user-authAction`}
                                    isIconOnly
                                    radius="full"
                                    size="sm"
                                    variant="light"
                                    onPress={userAuthAction}
                                >
                                    <Icon icon="ion:log-out-outline" width="24" height="24" style={{ color: "#667085" }} />
                                </CtyButton>
                            </div>
                        </div>
                    }
                </PopoverTrigger>
                <PopoverContent className='w-[250px] shadow-inherit'>
                    <Listbox aria-label="user-orgs" onAction={(selected) => switchOrganisation(selected.toString())} className='max-h-[70vh] overflow-y-auto'>
                        {(userOrgsList ?? []).map((org: OrgData, index: number) => {
                            const initials = getUserInitials(org.display_name);
                            return <ListboxItem key={org.name} title={org.display_name} value={org.display_name} endContent={<Icon icon="mynaui:refresh" width="18" height="18" style={{ color: "#667085" }} />}
                                startContent={<Avatar
                                    size={'md'}
                                    showFallback
                                    name={initials}
                                    alt="Profile"
                                    classNames={{ base: 'bg-[#f0f1f2] text-default-600', }}
                                />} />;
                        })}
                    </Listbox>
                </PopoverContent>
            </Popover>
        </div>
    );
};

export { SwitchOrgs };